import { forwardRef, useId, type InputHTMLAttributes } from 'react';
import { formStyles } from '@utils/formStyles';

interface FormInputProps extends InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
    helpText?: string;
    fullWidth?: boolean;
    noMargin?: boolean;
    /** Unit adornment rendered inside the input's right edge, e.g. "%" or "days" */
    suffix?: string;
}

const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
    ({ label, error, helpText, fullWidth = true, noMargin = false, suffix, className = '', id, ...rest }, ref) => {
        const generatedId = useId();
        const inputId = id ?? generatedId;
        const inputClasses = `${error ? formStyles.inputError : formStyles.input} ${formStyles.placeholder} ${suffix ? 'pr-10' : ''} ${className}`;

        let containerClasses;

        if (noMargin) {
            containerClasses = fullWidth ? formStyles.fullWidthContainerNoMargin : formStyles.containerNoMargin;
        } else {
            containerClasses = fullWidth ? formStyles.fullWidthContainer : formStyles.container;
        }

        return (
            <div className={containerClasses}>
                {label && (
                    <label className={formStyles.label} htmlFor={inputId}>
                        {label}
                    </label>
                )}
                {suffix ? (
                    <div className="relative">
                        <input className={inputClasses} id={inputId} ref={ref} {...rest} />
                        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-zinc-500">
                            {suffix}
                        </span>
                    </div>
                ) : (
                    <input className={inputClasses} id={inputId} ref={ref} {...rest} />
                )}
                {helpText && <div className={formStyles.helpText}>{helpText}</div>}
                {error && <div className={formStyles.errorText}>{error}</div>}
            </div>
        );
    }
);

FormInput.displayName = 'FormInput';

export default FormInput;
